import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLocation, useParams, Navigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import api from "../../../services/apiClient";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import Dropdown from "../../common/dropDown";
import { normListId } from "../../common/dualListBox";
import StudyAssignedStaffPanel from "../../common/StudyAssignedStaffPanel";
import StudyMetadataFields from "./StudyMetadataFields";
import {
  EMPTY_STUDY_METADATA,
  buildStudyMetadataPayload,
  getStudyMetadataValidationErrors,
  mapStudyMetadataFromInfo,
} from "./studyFormMetadataUtils";
import Accordion from "../../common/accordian/Accordion";
import "../../common/accordian/Accordion.scss";
import "../../common/StudyAssignedStaffPanel/StudyAssignedStaffPanel.scss";
import utils from "../../../utils/utils";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
} from "../../../utils/permissionUtils";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import StudyArchiveWizard from "./StudyArchiveWizard";
import { isStudyAlreadyArchived } from "./studyArchiveService";
import {
  ARCHIVED_STUDY_READONLY_MESSAGE,
  isArchivedStudyReadOnly,
} from "./studyArchivedReadOnly";
import {
  getActiveSiteLookup,
  getInternalActiveStaffLookup,
  siteToDropdownOptions,
  staffToDropdownOptions,
  staffMappedToAnySite,
} from "../../../services/lookupService";

const parseMappedSiteIds = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const staffRawToDualList = (staffList) =>
  staffToDropdownOptions(staffList).map((opt) => ({
    ...opt,
    userName:
      opt.staff?.StaffName ||
      `${opt.staff?.FirstName || ""} ${opt.staff?.LastName || ""}`.trim(),
    emailId: opt.staff?.EmailId || "",
  }));

/** Backend may return PascalCase or camelCase; pick first defined. */
const pick = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

/** Same as study save — API expects uppercase string IDs on StudyActivity. */
const idToUpperCase = (v) =>
  v === undefined || v === null || v === ""
    ? ""
    : String(v).toUpperCase();

const isApiSuccess = (res) => {
  if (!res || typeof res !== "object") return false;
  const s = res.success ?? res.Success;
  return (
    s === 1 ||
    s === true ||
    s === "1" ||
    String(s).toLowerCase() === "true"
  );
};

const normalizeToArray = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const vals = Object.values(raw);
    if (vals.length && typeof vals[0] === "object") return vals;
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) return p;
      if (p && typeof p === "object") return Object.values(p);
      return [];
    } catch {
      return [];
    }
  }
  return [];
};

/** Keys used for assigned staff / UserList (save payload uses `UserList` + `UserId`). */
const USER_ASSIGNMENT_KEYS = [
  "StudyUsers",
  "studyUsers",
  "UserList",
  "userList",
  "AssignedUsers",
  "assignedUsers",
];

/** StudyActivity envelope: inner object, nested data, or StudyInfo on root. */
const unwrapActivityPayload = (res) => {
  if (!res || typeof res !== "object") return {};
  let inner = res.data ?? res.Data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const nested = inner.data ?? inner.Data;
    if (
      nested &&
      typeof nested === "object" &&
      !Array.isArray(nested) &&
      (nested.StudyInfo != null ||
        nested.studyInfo != null ||
        nested.StudyUsers != null ||
        nested.studyUsers != null ||
        nested.UserList != null ||
        nested.userList != null)
    ) {
      /** When drilling into `data.data`, `UserList` often stays on the parent — merge it onto nested. */
      const merged = { ...nested };
      for (const k of USER_ASSIGNMENT_KEYS) {
        const childArr = normalizeToArray(merged[k]);
        if (!childArr.length && inner[k] != null) {
          merged[k] = inner[k];
        }
      }
      inner = merged;
    }
    return inner;
  }
  if (
    res.StudyInfo != null ||
    res.studyInfo != null ||
    res.StudyUsers != null ||
    res.studyUsers != null
  ) {
    return res;
  }
  const table = res.Table ?? res.table;
  if (Array.isArray(table) && table[0] && typeof table[0] === "object") {
    return table[0];
  }
  return {};
};

const getFirstStudyInfo = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const raw = pick(payload, "StudyInfo", "studyInfo", "Study", "study");
  if (Array.isArray(raw) && raw.length > 0) return raw[0];
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
  /** Some APIs return the study row flat on `data` with no StudyInfo wrapper. */
  if (
    pick(payload, "StudyNumber", "studyNumber") != null ||
    pick(payload, "StudyId", "studyId") != null ||
    pick(payload, "StudyName", "studyName") != null
  ) {
    return payload;
  }
  return null;
};

/** Users assigned to study — top-level on GetStudyInfo payload (or JSON string). */
const getStudyUsersList = (payload) => {
  if (!payload || typeof payload !== "object") return [];
  const raw = pick(
    payload,
    "StudyUsers",
    "studyUsers",
    "UserList",
    "userList",
    "AssignedUsers",
    "assignedUsers",
    "StudyStaffList",
    "studyStaffList",
    "AssignedStaffList",
    "assignedStaffList",
    "StaffAssignedList",
    "StudyUserList",
    "studyUserList",
  );
  return normalizeToArray(raw);
};

const getStudyUsersListFromStudyInfo = (info) => {
  if (!info || typeof info !== "object") return [];
  const raw = pick(
    info,
    "StudyUsers",
    "studyUsers",
    "UserList",
    "userList",
    "AssignedUsers",
    "assignedUsers",
  );
  return normalizeToArray(raw);
};

/** If unwrapped `data` omits StudyUsers but the HTTP envelope has it, merge (GetStudyInfo variants). */
const mergeStudyUsersFromEnvelope = (data, res) => {
  if (!data || typeof data !== "object") return data;
  const hasUsers =
    getStudyUsersList(data).length > 0 ||
    getStudyUsersListFromStudyInfo(getFirstStudyInfo(data)).length > 0;
  if (hasUsers) return data;
  const fromRoot = [
    ...getStudyUsersList(res),
    ...(res?.data ? getStudyUsersList(res.data) : []),
    ...(res?.Data ? getStudyUsersList(res.Data) : []),
  ];
  if (!fromRoot.length) return data;
  return { ...data, StudyUsers: fromRoot };
};

const isAssignedUserRow = (u) => {
  if (u == null || typeof u !== "object") return false;
  const v = u.IsAssigned ?? u.isAssigned;
  /** Empty string / whitespace = not specified → treat as assigned. */
  if (v === undefined || v === null || v === "") return true;
  if (v === false || v === 0 || v === "0") return false;
  return (
    v === true ||
    v === 1 ||
    v === "1" ||
    String(v).toLowerCase() === "true" ||
    String(v).toLowerCase() === "yes"
  );
};

/** Id fields — `UserList` rows from save use `UserId` first (see StudyActivity payload). */
const pickUserRowStaffOrUserId = (u) =>
  pick(
    u,
    "UserId",
    "userId",
    "StaffId",
    "staffId",
    "StaffID",
    "StaffIDRef",
    "staffIDRef",
    "UserIdRef",
    "userIdRef",
    "StaffRef",
    "staffRef",
    "STAFF_ID",
    "UserID",
    "UserIDRef",
    "EmployeeId",
    "employeeId",
    "Staff_Id",
    "staff_Id",
  );

/** When API sends UserId that is not StaffId, match via EmailId ↔ org staff list. */
const mergeStaffIdsByEmail = (userRows, staffListItems, ids, seen) => {
  const emailToValue = new Map();
  for (const s of staffListItems || []) {
    const em = String(s.emailId || s.EmailId || "").trim().toLowerCase();
    if (em) emailToValue.set(em, normListId(s.value));
  }
  for (const u of userRows) {
    if (!u || typeof u !== "object") continue;
    const em = String(
      pick(u, "EmailId", "emailId", "Email", "email") || "",
    )
      .trim()
      .toLowerCase();
    if (!em || !emailToValue.has(em)) continue;
    const id = emailToValue.get(em);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
};

/** Merge study users from payload + StudyInfo + raw API envelope (data may nest under different keys). */
const collectAssignedStaffIdsFromPayload = (
  payload,
  staffListItems,
  rawEnvelope,
) => {
  const ids = [];
  const seen = new Set();

  const pushRows = (rows, respectAssignFlag) => {
    if (!Array.isArray(rows)) return;
    for (const u of rows) {
      if (respectAssignFlag && !isAssignedUserRow(u)) continue;
      const id = normListId(pickUserRowStaffOrUserId(u));
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  };

  const topUsers = [
    ...getStudyUsersList(payload),
    ...(rawEnvelope ? getStudyUsersList(rawEnvelope) : []),
    ...(rawEnvelope?.data ? getStudyUsersList(rawEnvelope.data) : []),
    ...(rawEnvelope?.Data ? getStudyUsersList(rawEnvelope.Data) : []),
  ];
  const info = getFirstStudyInfo(payload);
  const nestedUsers = getStudyUsersListFromStudyInfo(info);
  const allUserRows = [...topUsers, ...nestedUsers];

  pushRows(allUserRows, true);
  if (ids.length === 0 && allUserRows.length > 0) {
    pushRows(allUserRows, false);
  }

  mergeStaffIdsByEmail(allUserRows, staffListItems, ids, seen);

  /** PI is often on the study row but omitted from StudyUsers — show in Selected. */
  if (info) {
    const piId = normListId(
      pick(
        info,
        "PrincipalInvestigator",
        "principalInvestigator",
        "PI",
        "pi",
        "PIStaffId",
        "piStaffId",
      ),
    );
    if (piId && !seen.has(piId)) {
      seen.add(piId);
      ids.push(piId);
    }
  }

  return ids;
};

const StudyProfile = () => {
  const location = useLocation();
  const { studyId } = useParams();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  /** StaffIds loaded from server — used for RemovedUserList (same as Study Management modal). */
  const initialAssignedStaffIdsRef = useRef([]);

  const isSystemAdmin = userData?.Role === "System Administrator";
  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");
  const canViewStudyList =
    isSystemAdmin || hasPermission(userPermissions, "STUDY_LIST");
  const canEditStudy =
    !!userData &&
    (isSystemAdmin || hasPermission(userPermissions, "EDIT_STUDY"));

  const initialStudy = location.state?.study || null;
  const [study, setStudy] = useState(initialStudy);
  const [activeTab, setActiveTab] = useState("studyInfo");
  /** When true, changing sites auto-assigns all mapped staff (create-style). Skipped on initial load. */
  const staffAutoAssignEnabledRef = useRef(false);
  /** Full org staff for dual list (available + selected). */
  const [allStaffRaw, setAllStaffRaw] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  /** Start true when route has id so we don't flash empty form before fetch. */
  const [isLoadingStudyInfo, setIsLoadingStudyInfo] = useState(() =>
    Boolean(studyId),
  );
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [studyColorOptions] = useState(() => utils.studyColors());
  const [statusOptions] = useState([
    { value: "Live", label: "Live" },
  ]);
  const [archiveWizardOpen, setArchiveWizardOpen] = useState(false);
  const [studyForm, setStudyForm] = useState({
    studyNumber: "",
    studyTitle: "",
    studyName: "",
    status: "",
    studyColor: "",
    piName: "",
    eudractNumber: "",
    rdNumber: "",
    department: "",
    siteId: "",
    sponsorName: "",
    irasNumber: "",
    groupType: "",
    createdDateTime: "",
    coordinatorName: "",
    coordinatorEmail: "",
    coordinatorContact: "",
    /** PrincipalInvestigator StaffId from API (for PI dropdown). */
    piStaffId: "",
    ...EMPTY_STUDY_METADATA,
  });
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);

  const isStudyArchived = useMemo(
    () =>
      isArchivedStudyReadOnly(study) ||
      isArchivedStudyReadOnly(initialStudy) ||
      isArchivedStudyReadOnly({ StudyStatus: studyForm.status }),
    [study, initialStudy, studyForm.status],
  );
  const canMutateStudy = canEditStudy && !isStudyArchived;
  const statusDropdownOptions = useMemo(() => {
    if (!isStudyArchived) return statusOptions;
    return [{ value: "Archived", label: "Archived" }];
  }, [isStudyArchived, statusOptions]);

  const selectedSiteIds = useMemo(
    () => (selectedSites || []).map((site) => site.value).filter(Boolean),
    [selectedSites],
  );

  const siteFilteredStaff = useMemo(() => {
    if (!selectedSiteIds.length) return [];
    return allStaffRaw.filter((staff) =>
      staffMappedToAnySite(staff, selectedSiteIds),
    );
  }, [allStaffRaw, selectedSiteIds]);

  const fullStaffList = useMemo(() => {
    const byId = new Map();
    siteFilteredStaff.forEach((staff) => {
      byId.set(String(staff.StaffId).toLowerCase(), staff);
    });
    selectedStaffIds.forEach((staffId) => {
      const match = allStaffRaw.find(
        (staff) =>
          String(staff.StaffId).toLowerCase() === String(staffId).toLowerCase(),
      );
      if (match) byId.set(String(match.StaffId).toLowerCase(), match);
    });
    return staffRawToDualList([...byId.values()]);
  }, [siteFilteredStaff, allStaffRaw, selectedStaffIds]);

  const principalOptions = useMemo(
    () =>
      staffRawToDualList(
        siteFilteredStaff.filter(
          (staff) =>
            String(staff?.RoleName || staff?.Roles || "")
              .trim()
              .toLowerCase() === "principal investigator",
        ),
      ),
    [siteFilteredStaff],
  );

  /** Selected dual-list rows (full objects) for validation + StudyActivity UserList. */
  const assignedStaff = useMemo(() => {
    const idSet = new Set(
      selectedStaffIds
        .map((id) => String(normListId(id)).toLowerCase())
        .filter(Boolean),
    );
    return fullStaffList.filter((s) => {
      const nv = normListId(s.value);
      return nv && idSet.has(String(nv).toLowerCase());
    });
  }, [fullStaffList, selectedStaffIds]);

  useEffect(() => {
    if (initialStudy) setStudy(initialStudy);
  }, [initialStudy]);

  /** Auto-assign all staff mapped to selected site(s) after user changes sites. */
  useEffect(() => {
    if (!staffAutoAssignEnabledRef.current) return;

    if (!selectedSiteIds.length) {
      setSelectedStaffIds([]);
      return;
    }

    setSelectedStaffIds(
      siteFilteredStaff
        .map((staff) => normListId(staff.StaffId))
        .filter(Boolean),
    );
  }, [selectedSiteIds, siteFilteredStaff]);

  const handleToggleStudyStaff = useCallback((staffRow, shouldAdd) => {
    const staffId = normListId(staffRow?.StaffId);
    if (!staffId) return;

    setSelectedStaffIds((prev) => {
      const normalized = String(staffId).toLowerCase();
      if (shouldAdd) {
        if (prev.some((id) => String(id).toLowerCase() === normalized)) {
          return prev;
        }
        return [...prev, staffId];
      }
      return prev.filter((id) => String(id).toLowerCase() !== normalized);
    });

    if (!shouldAdd) {
      setStudyForm((prev) => {
        if (String(prev.piStaffId).toLowerCase() === String(staffId).toLowerCase()) {
          return { ...prev, piStaffId: "", piName: "" };
        }
        return prev;
      });
    }
  }, []);

  const mapStudyInfoToForm = useCallback((info, fallbackStudy) => {
    const piId = pick(
      info,
      "PrincipalInvestigator",
      "principalInvestigator",
      "PI",
      "pi",
      "PIStaffId",
      "piStaffId",
    );
    const studyNum = pick(info, "StudyNumber", "studyNumber");
    const studyName = pick(info, "StudyName", "studyName");
    const status = pick(info, "StudyStatus", "studyStatus");
    const color = pick(
      info,
      "StudyColor",
      "studyColor",
      "StudySchColor",
      "studySchColor",
    );
    const tg = pick(info, "TotalGroup", "totalGroup");
    const gt = pick(info, "GroupType", "groupType");
    const groupType =
      tg != null && tg !== "" && String(tg) !== "0"
        ? `${(gt || fallbackStudy?.GroupType || "").trim()} (${tg} groups)`
        : gt || fallbackStudy?.GroupType || "";
    return {
      studyNumber: studyNum != null ? String(studyNum) : "",
      studyTitle: studyName || "",
      studyName: studyName || "",
      status: status || "",
      studyColor: color || "",
      piStaffId:
        piId !== undefined && piId !== null ? String(piId) : "",
      piName: "",
      eudractNumber: pick(info, "EudractNumber", "eudractNumber") || "",
      rdNumber:
        pick(info, "RandNumber", "randNumber", "RDNumber", "rdNumber") ?? "",
      department:
        pick(info, "Department", "department") != null
          ? String(pick(info, "Department", "department"))
          : "",
      siteId: (() => {
        const v = pick(
          info,
          "SiteID",
          "siteID",
          "SiteId",
          "siteId",
          "Site_ID",
          "site_id",
          "StudySiteID",
          "studySiteID",
          "SiteCode",
          "siteCode",
          "SiteRef",
          "siteRef",
        );
        if (v === undefined || v === null) return "";
        return String(v);
      })(),
      sponsorName: pick(info, "SponsorName", "sponsorName") || "",
      irasNumber: pick(info, "IRASNumber", "irasNumber") || "",
      groupType,
      createdDateTime:
        pick(info, "SystemDateTime", "systemDateTime") ??
        fallbackStudy?.SystemDateTime ??
        "",
      coordinatorName:
        pick(info, "CordinatorName", "cordinatorName", "CoordinatorName") || "",
      coordinatorEmail:
        pick(info, "CordinatorEmail", "cordinatorEmail", "CoordinatorEmail") ||
        "",
      coordinatorContact:
        pick(
          info,
          "CordinatorPhone",
          "cordinatorPhone",
          "CoordinatorPhone",
        ) || "",
      ...mapStudyMetadataFromInfo(info),
    };
  }, []);

  /** List-row / navigation state → form fields when GetStudyInfo is missing or fails. */
  const mapNavStudyRowToForm = useCallback((row) => {
    if (!row) return null;
    return {
      studyNumber: row.StudyNumber ?? "",
      studyTitle: row.StudyTitle || row.StudyName || "",
      studyName: row.StudyName || "",
      status: row.StudyStatus || "",
      studyColor: row.StudySchColor || "",
      piStaffId:
        row.PrincipalInvestigator != null && row.PrincipalInvestigator !== ""
          ? String(row.PrincipalInvestigator)
          : row.PIStaffId != null && row.PIStaffId !== ""
            ? String(row.PIStaffId)
            : "",
      piName: row.PIName || "",
      eudractNumber: row.EudractNumber || "",
      rdNumber: row.RDNumber || row.RDNo || "",
      department: row.DepartmentName || "",
      
      siteId: (() => {
        const v = pick(
          row,
          "SiteId",
          "SiteID",
          "siteId",
          "Site_ID",
          "StudySiteID",
          "studySiteID",
          "SiteCode",
        );
        if (v === undefined || v === null) return "";
        return String(v);
      })(),
      sponsorName: row.SponsorName || "",
      irasNumber: row.IRASNumber || "",
      groupType: row.TotalGroup
        ? `${row.GroupType || ""} (${row.TotalGroup} groups)`
        : row.GroupType || "",
      createdDateTime: row.SystemDateTime || "",
      coordinatorName: row.CordinatorName || "",
      coordinatorEmail: row.CordinatorEmail || "",
      coordinatorContact: row.CordinatorContact || "",
    };
  }, []);

  // Load common dropdowns + full staff list, then GetStudyInfo (StudyActivity)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!userData?.OrganizationId || !userData?.StaffId) {
        setIsLoadingStudyInfo(false);
        return;
      }

      const studyIdVal =
        studyId ||
        initialStudy?.StudyId ||
        initialStudy?.StudyIdRef ||
        initialStudy?.StudyNumber;
      if (!studyIdVal) {
        setIsLoadingStudyInfo(false);
        return;
      }

      setIsLoadingStudyInfo(true);
      showLoader("Study Info loading");
      try {
        const [commonRes, sites, staffRaw] = await Promise.all([
          api.post(API_ENDPOINTS.USER.GET_USER_PROFILE_COMMONDATA, {
            OrganizationId: userData.OrganizationId,
            StaffIDRef: userData.StaffId,
            CreatedBy: userData.StaffId,
            PageName: "AddStudyReact",
          }),
          getActiveSiteLookup(userData, {}, { forceRefresh: true }),
          getInternalActiveStaffLookup(userData, {}, { forceRefresh: true }),
        ]);
        if (cancelled) return;

        let commonPayload = commonRes.data ?? commonRes.Data;
        if (
          !commonPayload &&
          (commonRes.DepartmentList ||
            commonRes.departmentList)
        ) {
          commonPayload = commonRes;
        }

        const departmentsData = utils.mapToOptions(
          commonPayload?.DepartmentList || commonPayload?.departmentList || [],
          "DepartmentName",
          "DepartmentId",
        );
        const siteOpts = siteToDropdownOptions(sites);
        const tempStaffFilterData = staffRawToDualList(staffRaw);

        setDepartmentOptions(departmentsData);
        setSiteOptions(siteOpts);
        setAllStaffRaw(staffRaw);

        const studyRes = await api.post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
          OrganizationId: idToUpperCase(userData.OrganizationId),
          CreatedBy: idToUpperCase(userData.StaffId),
          StaffIdRef: idToUpperCase(userData.StaffId),
          StudyId: idToUpperCase(studyIdVal),
          Action: "GetStudyInfo",
        });
        if (cancelled) return;

        const data = mergeStudyUsersFromEnvelope(
          unwrapActivityPayload(studyRes),
          studyRes,
        );

        const info = getFirstStudyInfo(data);
        const assignedStaffIds = collectAssignedStaffIdsFromPayload(
          data,
          tempStaffFilterData,
          studyRes,
        );

        const applyNavFallback = () => {
          if (!initialStudy) return;
          setStudy(initialStudy);
          const navFields = mapNavStudyRowToForm(initialStudy);
          if (!navFields) return;
          const piStaff = staffRaw.find(
            (staff) =>
              String(staff.StaffId) === String(navFields.piStaffId),
          );
          const piLabel = piStaff
            ? staffRawToDualList([piStaff])[0]?.label
            : navFields.piName;
          setStudyForm((prev) => ({
            ...prev,
            ...navFields,
            piName: piLabel || navFields.piName,
          }));
          const fallbackSiteId = navFields.siteId;
          if (fallbackSiteId) {
            const matchedSite = siteOpts.find(
              (opt) =>
                String(opt.value).toLowerCase() ===
                String(fallbackSiteId).toLowerCase(),
            );
            if (matchedSite) setSelectedSites([matchedSite]);
          }
        };

        if (!isApiSuccess(studyRes)) {
          applyNavFallback();
          return;
        }

        if (!info && initialStudy) {
          applyNavFallback();
          return;
        }
        if (!info) {
          applyNavFallback();
          return;
        }

        const navStudy = location.state?.study || null;
        const form = mapStudyInfoToForm(info, navStudy);
        const piStaff = staffRaw.find(
          (staff) => String(staff.StaffId) === String(form.piStaffId),
        );
        if (piStaff) {
          form.piName = staffRawToDualList([piStaff])[0]?.label || form.piName;
        }

        const deptVal = pick(info, "Department", "department");
        const deptOpt = departmentsData.find(
          (o) => String(o.value) === String(deptVal),
        );
        if (deptOpt) {
          form.department = deptOpt.label;
        } else if (deptVal != null && deptVal !== "") {
          form.department = String(deptVal);
        }

        let mappedSiteIds = parseMappedSiteIds(
          pick(info, "MappedSiteIds", "mappedSiteIds"),
        );
        if (!mappedSiteIds.length) {
          const siteExtra =
            pick(
              data,
              "SiteID",
              "SiteId",
              "siteId",
              "siteID",
              "StudySiteID",
              "studySiteID",
            ) ||
            pick(
              info,
              "SiteID",
              "SiteId",
              "siteId",
              "siteID",
              "StudySiteID",
              "studySiteID",
            ) ||
            form.siteId;
          if (siteExtra != null && siteExtra !== "") {
            mappedSiteIds = [String(siteExtra)];
          }
        }
        const matchedSites = siteOpts.filter((opt) =>
          mappedSiteIds.some(
            (siteId) =>
              String(siteId).toLowerCase() === String(opt.value).toLowerCase(),
          ),
        );
        setSelectedSites(matchedSites);
        if (matchedSites[0]?.value) {
          form.siteId = String(matchedSites[0].value);
        }

        setStudyForm(form);

        const sn = pick(info, "StudyNumber", "studyNumber");
        const sname = pick(info, "StudyName", "studyName");
        const sst = pick(info, "StudyStatus", "studyStatus");
        const sch = pick(
          info,
          "StudyColor",
          "studyColor",
          "StudySchColor",
          "studySchColor",
        );
        const pin = pick(info, "PIName", "pIName", "piName");
        const gType = pick(info, "GroupType", "groupType");
        const tGroup = pick(info, "TotalGroup", "totalGroup");

        setStudy({
          StudyId: studyIdVal,
          StudyNumber: sn,
          StudyName: sname,
          StudyStatus: sst,
          StudySchColor: sch,
          PIName: form.piName || pin || "",
          GroupType: gType ?? navStudy?.GroupType,
          TotalGroup: tGroup ?? navStudy?.TotalGroup,
          SystemDateTime: form.createdDateTime || navStudy?.SystemDateTime,
        });

        setSelectedStaffIds(assignedStaffIds);
        initialAssignedStaffIdsRef.current = assignedStaffIds
          .map((id) => normListId(id))
          .filter(Boolean);
      } catch (err) {
        console.error("Error loading study profile:", err);
        if (!cancelled && initialStudy) {
          setStudy(initialStudy);
          const navFields = mapNavStudyRowToForm(initialStudy);
          if (navFields) {
            setStudyForm((prev) => ({ ...prev, ...navFields }));
          }
        }
      } finally {
        if (!cancelled) {
          hideLoader();
          setIsLoadingStudyInfo(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    userData?.OrganizationId,
    userData?.StaffId,
    studyId,
    mapStudyInfoToForm,
    mapNavStudyRowToForm,
    location.state?.study,
  ]);

  // Fallback: sync form from navigation state when API did not run (no studyId)
  useEffect(() => {
    if (isLoadingStudyInfo || !initialStudy) return;
    const sid =
      studyId ||
      initialStudy?.StudyId ||
      initialStudy?.StudyIdRef ||
      initialStudy?.StudyNumber;
    if (sid) return;

    setStudyForm({
      studyNumber: initialStudy.StudyNumber || "",
      studyTitle: initialStudy.StudyTitle || initialStudy.StudyName || "",
      studyName: initialStudy.StudyName || "",
      status: initialStudy.StudyStatus || "",
      studyColor: initialStudy.StudySchColor || "",
      piName: initialStudy.PIName || "",
      piStaffId: "",
      eudractNumber: initialStudy.EudractNumber || "",
      rdNumber: initialStudy.RDNumber || initialStudy.RDNo || "",
      department: initialStudy.DepartmentName || "",
      siteId: initialStudy.SiteId || initialStudy.SiteID || "",
      sponsorName: initialStudy.SponsorName || "",
      irasNumber: initialStudy.IRASNumber || "",
      groupType: initialStudy.TotalGroup
        ? `${initialStudy.GroupType || ""} (${initialStudy.TotalGroup} groups)`
        : initialStudy.GroupType || "",
      createdDateTime: initialStudy.SystemDateTime || "",
      coordinatorName: initialStudy.CordinatorName || "",
      coordinatorEmail: initialStudy.CordinatorEmail || "",
      coordinatorContact: initialStudy.CordinatorContact || "",
    });
  }, [initialStudy, isLoadingStudyInfo, studyId]);

  const handleSitesChange = (selected) => {
    staffAutoAssignEnabledRef.current = true;
    setSelectedSites(Array.isArray(selected) ? selected : []);
  };

  const handleStudyInput = (e) => {
    const { name, value } = e.target;
    setStudyForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "studyTitle" ? { studyName: value } : {}),
    }));
  };

  /** Same rules as `studymanagement.jsx` `getStudyBasicValidationErrors`. */
  const getStudyBasicValidationErrors = () => {
    const errs = [];
    if (!String(studyForm.studyNumber ?? "").trim()) {
      errs.push("Study number is required.");
    }
    if (!String(studyForm.studyTitle ?? "").trim()) {
      errs.push("Study title is required.");
    }
    const colorVal =
      studyForm.studyColor &&
      (typeof studyForm.studyColor === "object"
        ? studyForm.studyColor.value
        : studyForm.studyColor);
    if (colorVal === undefined || colorVal === null || colorVal === "") {
      errs.push("Study color is required.");
    }
    const piId = studyForm.piStaffId;
    if (piId === undefined || piId === null || piId === "") {
      errs.push("Principal Investigator is required.");
    }
    if (!selectedSites.length) {
      errs.push("At least one site is required.");
    }
    if (selectedSites.length > 0 && !siteFilteredStaff.length) {
      errs.push("No staff are mapped to the selected site(s).");
    }
    if (!assignedStaff.length && siteFilteredStaff.length) {
      errs.push("Assign at least one staff member.");
    }
    if (
      piId !== undefined &&
      piId !== null &&
      piId !== "" &&
      !assignedStaff.some((s) => String(s.value) === String(piId))
    ) {
      errs.push(
        "Principal Investigator must be included in the Assign Staff list.",
      );
    }
    const piStaff = allStaffRaw.find(
      (staff) => String(staff.StaffId) === String(piId),
    );
    if (piStaff && !staffMappedToAnySite(piStaff, selectedSiteIds)) {
      errs.push(
        "Principal Investigator must be mapped to a selected study site.",
      );
    }
    const invalidAssigned = assignedStaff.filter((row) => {
      const staff = allStaffRaw.find(
        (item) =>
          String(item.StaffId).toLowerCase() === String(row.value).toLowerCase(),
      );
      return staff && !staffMappedToAnySite(staff, selectedSiteIds);
    });
    if (invalidAssigned.length) {
      errs.push(
        "One or more assigned staff are not mapped to the selected study site(s).",
      );
    }
    errs.push(...getStudyMetadataValidationErrors(studyForm, { isCreating: false }));
    return errs;
  };

  const handleStudyMetadataChange = (field, value) => {
    setStudyForm((prev) => ({ ...prev, [field]: value }));
  };

  /** Build UserList row from StaffId using org staff lookup. */
  const staffUserRowFromId = useCallback(
    (staffId, isAssigned) => {
      const staff = allStaffRaw.find(
        (item) => normListId(item.StaffId) === normListId(staffId),
      );
      if (!staff) return null;
      const row = staffRawToDualList([staff])[0];
      return {
        UserId: idToUpperCase(row.value),
        UserName: (row.userName ?? "").trim(),
        EmailId: (row.emailId ?? "").trim(),
        IsAssigned: !!isAssigned,
      };
    },
    [allStaffRaw],
  );

  const refreshAssignedStaffFromServer = useCallback(
    async (studyIdVal) => {
      if (!userData?.OrganizationId || !userData?.StaffId || !studyIdVal) {
        return;
      }
      const staffListItems = staffRawToDualList(allStaffRaw);
      const studyRes = await api.post(API_ENDPOINTS.STUDY.STUDY_ACTIVITY, {
        OrganizationId: idToUpperCase(userData.OrganizationId),
        CreatedBy: idToUpperCase(userData.StaffId),
        StaffIdRef: idToUpperCase(userData.StaffId),
        StudyId: idToUpperCase(studyIdVal),
        Action: "GetStudyInfo",
      });
      const data = mergeStudyUsersFromEnvelope(
        unwrapActivityPayload(studyRes),
        studyRes,
      );
      const assignedStaffIds = collectAssignedStaffIdsFromPayload(
        data,
        staffListItems,
        studyRes,
      );
      setSelectedStaffIds(assignedStaffIds);
      initialAssignedStaffIdsRef.current = assignedStaffIds
        .map((id) => normListId(id))
        .filter(Boolean);
    },
    [allStaffRaw, userData?.OrganizationId, userData?.StaffId],
  );

  /** Same shape as `studymanagement.jsx` `buildStudyActivityPayload` (Update). */
  const buildStudyActivityPayload = () => {
    const studyNumDigitsOnly = String(studyForm.studyNumber ?? "")
      .replace(/\D/g, "")
      .trim();
    const colorHex =
      studyForm.studyColor &&
      (typeof studyForm.studyColor === "object"
        ? studyForm.studyColor.value
        : studyForm.studyColor);

    const currentIdSet = new Set(
      selectedStaffIds.map((id) => normListId(id)).filter(Boolean),
    );

    /** UserList = currently selected staff only (SP deactivates anyone not in this list). */
    const userList = selectedStaffIds
      .map((staffId) => staffUserRowFromId(staffId, true))
      .filter(Boolean);

    const removedUserList = [];
    initialAssignedStaffIdsRef.current.forEach((rawId) => {
      const id = normListId(rawId);
      if (!id || currentIdSet.has(id)) return;
      const removed = staffUserRowFromId(rawId, false);
      if (removed) removedUserList.push(removed);
    });

    const resolvedStudyId =
      study?.StudyId ||
      studyId ||
      initialStudy?.StudyId ||
      initialStudy?.StudyIdRef ||
      "";

    const departmentForApi = (() => {
      const d = studyForm.department;
      if (d === undefined || d === null || d === "") return "";
      const byLabel = departmentOptions.find(
        (o) => String(o.label) === String(d),
      );
      if (byLabel != null && byLabel.value !== undefined) {
        return String(byLabel.value);
      }
      const byVal = departmentOptions.find(
        (o) => String(o.value) === String(d),
      );
      if (byVal != null) return String(byVal.value ?? "");
      return String(d);
    })();

    const primarySiteId = selectedSites[0]?.value || "";

    return {
      OrganizationId: idToUpperCase(userData.OrganizationId),
      CreatedBy: idToUpperCase(userData.StaffId),
      StudyId: idToUpperCase(resolvedStudyId),
      Action: "Update",
      Study: (studyForm.studyTitle || "").trim(),
      StudyNum: studyNumDigitsOnly,
      PI: idToUpperCase(studyForm.piStaffId),
      EudractNumber: (studyForm.eudractNumber || "").trim(),
      Randnumber: (studyForm.rdNumber || "").trim(),
      SiteID: String(primarySiteId).trim(),
      SiteList: selectedSites.map((site) => ({
        SiteId: idToUpperCase(site.value),
        SiteName: site.label || site.site?.SiteName || "",
      })),
      Department: departmentForApi,
      StudyColor: colorHex ?? "",
      Status: String(studyForm.status || ""),
      UserList: userList,
      RemovedUserList: removedUserList,
      StudyLocationList: [],
      ProjectId: "",
      SponsorName: (studyForm.sponsorName || "").trim(),
      IRASNumber: (studyForm.irasNumber || "").trim(),
      CordinatorName: (studyForm.coordinatorName || "").trim(),
      CordinatorPhone: (studyForm.coordinatorContact || "").trim(),
      CordinatorEmail: (studyForm.coordinatorEmail || "").trim(),
      ...buildStudyMetadataPayload(studyForm),
    };
  };

  const handleSaveStudy = async () => {
    if (isStudyArchived) {
      showAlert({
        title: "Archived study",
        message: ARCHIVED_STUDY_READONLY_MESSAGE,
        type: "info",
      });
      return;
    }

    const resolvedStudyId =
      study?.StudyId ||
      studyId ||
      initialStudy?.StudyId ||
      initialStudy?.StudyIdRef ||
      "";
    if (!String(resolvedStudyId).trim()) {
      showAlert({
        title: "Study",
        message:
          "Study id is missing. Open this study from Study Management and try again.",
        type: "warning",
      });
      return;
    }

    const basicErrs = getStudyBasicValidationErrors();
    if (basicErrs.length > 0) {
      showAlert({
        title: "Study details",
        message: basicErrs.join("\n"),
        type: "warning",
      });
      return;
    }

    const statusValue = String(studyForm.status || "").trim().toLowerCase();
    if (statusValue === "archive" || statusValue === "archived") {
      if (isStudyAlreadyArchived(study || initialStudy)) {
        showAlert({
          title: "Archive study",
          message: "This study is already archived.",
          type: "warning",
        });
        return;
      }
      setArchiveWizardOpen(true);
      return;
    }

    try {
      showLoader("Saving study...");
      const payload = buildStudyActivityPayload();
      const response = await api.post(
        API_ENDPOINTS.STUDY.STUDY_ACTIVITY,
        payload,
      );
      if (utils.isApiSuccess(response)) {
        const resolvedStudyId =
          study?.StudyId ||
          studyId ||
          initialStudy?.StudyId ||
          initialStudy?.StudyIdRef ||
          "";
        await refreshAssignedStaffFromServer(resolvedStudyId);
        showAlert({
          title: "Study",
          message: utils.getApiResponseMessage(
            response,
            "Study updated successfully.",
          ),
          type: "success",
        });
      } else {
        showAlert({
          title: "Study",
          message: utils.getApiResponseMessage(
            response,
            "Study could not be updated.",
          ),
          type: "warning",
        });
      }
    } catch (err) {
      console.error("Study save error:", err);
      showAlert({
        title: "Study",
        message: "An error occurred while saving the study.",
        type: "warning",
      });
    } finally {
      hideLoader();
    }
  };

  if (!userData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!canViewStudyList) {
    return <Navigate to="/notification-center" replace />;
  }

  if (isLoadingStudyInfo) {
    return null;
  }

  return (
    <div className="user-profile-page p-2">
      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Study Management", path: "/studymanagement" },
          { label: "Study Profile" },
        ]}
      />

      <main className="user-profile-main">
        <div className="user-profile-container">
          {/* Profile Sidebar (mirrors UserProfile layout, but with study fields) */}
          <aside className="profile-sidebar">
            <div className="profile-sidebar-content">
              <div className="profile-image-wrapper">
                <span
                  className="profile-emp-badge"
                  title={`Study #: ${study?.StudyNumber || studyId || "N/A"}`}
                >
                  {study?.StudyNumber || studyId || "N/A"}
                </span>
                <h3
                  className="profile-name"
                  title={study?.StudyName || "Study name"}
                >
                  {study?.StudyName || "Study name"}
                </h3>
              </div>
              <div className="profile-info">
                <div className="profile-role-department">
                  <i className="fas fa-vial"></i>
                  <span>
                    {study?.StudyStatus || "N/A"}
                  </span>
                </div>
                {study?.GroupType && (
                  <div className="profile-organization">
                    <i className="fas fa-users"></i>
                    <span>
                      {study.GroupType}
                      {study.TotalGroup
                        ? ` (${study.TotalGroup} groups)`
                        : null}
                    </span>
                  </div>
                )}
                {study?.PIName && (
                  <div className="profile-contact">
                    <i className="fas fa-user-md"></i>
                    <span>PI: {study.PIName}</span>
                  </div>
                )}
                {study?.SystemDateTime && (
                  <div className="profile-last-login">
                    <i className="fas fa-clock"></i>
                    <span>Created: {study.SystemDateTime}</span>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Tabs + content (more tabs can be added later) */}
          <div className="profile-main">
            <div className="profile-tabs">
              <button
                type="button"
                className={`tab-item ${
                  activeTab === "studyInfo" ? "active" : ""
                }`}
                onClick={() => setActiveTab("studyInfo")}
              >
                <i className="fas fa-info-circle"></i>
                <span>Study Info</span>
              </button>
            </div>

            <div className="content-grid">
              <div className="content-left">
                {activeTab === "studyInfo" && (
                  <div className="section-card section-card--general">
                  <div className="section-header d-flex justify-content-between align-items-center">
                    <h3>Study Information</h3>
                    {canMutateStudy && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSaveStudy}
                        disabled={!study && !studyForm.studyTitle}
                      >
                        <i className="fas fa-save me-2"></i>
                        Update
                      </button>
                    )}
                  </div>
                  <div className="section-body">
                    {isStudyArchived ? (
                      <div className="study-archived-alert" role="status">
                        <i className="fas fa-archive" aria-hidden="true" />
                        <span>{ARCHIVED_STUDY_READONLY_MESSAGE}</span>
                      </div>
                    ) : null}
                    {study ||
                    studyForm.studyTitle ||
                    studyForm.studyNumber ? (
                      <div className="study-add-edit-layout study-add-edit-layout--profile">
                        <div className="study-add-edit-layout__form">
                          <div className="study-form-grid">
                            <div className="study-form-field">
                              <label>
                                Study Number{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <div className="span-under-input study-form-prefix-input">
                                <span>STD</span>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="studyNumber"
                                  value={studyForm.studyNumber}
                                  onChange={handleStudyInput}
                                  disabled={!canMutateStudy}
                                  readOnly={!canMutateStudy}
                                />
                              </div>
                            </div>
                            <div className="study-form-field">
                              <label>
                                Study Title{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                name="studyTitle"
                                value={studyForm.studyTitle}
                                onChange={handleStudyInput}
                                disabled={!canMutateStudy}
                                readOnly={!canMutateStudy}
                              />
                            </div>
                            <div className="study-form-field">
                              <label>
                                Study Color Code{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={studyColorOptions}
                                isMulti={false}
                                isColorDropDown={true}
                                disabled={!canMutateStudy}
                                selected={studyColorOptions.find(
                                  (opt) =>
                                    String(opt.value).toLowerCase() ===
                                    String(
                                      studyForm.studyColor || "",
                                    ).toLowerCase(),
                                )}
                                onSelect={(opt) =>
                                  setStudyForm((prev) => ({
                                    ...prev,
                                    studyColor: opt?.value || "",
                                  }))
                                }
                              />
                            </div>
                            <div className="study-form-field">
                              <label>
                                Sites <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={siteOptions}
                                isMulti={true}
                                disabled={!canMutateStudy}
                                selected={selectedSites}
                                onSelect={handleSitesChange}
                              />
                            </div>
                            <div className="study-form-field">
                              <label>
                                Principal Investigator{" "}
                                <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={principalOptions}
                                isMulti={false}
                                disabled={!canMutateStudy}
                                selected={principalOptions.find(
                                  (opt) =>
                                    String(opt.value) ===
                                    String(studyForm.piStaffId || ""),
                                )}
                                onSelect={(opt) =>
                                  setStudyForm((prev) => ({
                                    ...prev,
                                    piStaffId: opt?.value ?? "",
                                    piName: opt?.label || "",
                                  }))
                                }
                              />
                            </div>

                            <div className="study-form-field study-form-field--section">
                              <span className="study-form-optional-label">Optional</span>
                            </div>

                            <div className="study-form-field">
                              <label>Department</label>
                              <Dropdown
                                options={departmentOptions}
                                isMulti={false}
                                disabled={!canMutateStudy}
                                selected={departmentOptions.find(
                                  (opt) =>
                                    String(opt.label) ===
                                      String(studyForm.department || "") ||
                                    String(opt.value) ===
                                      String(studyForm.department || ""),
                                )}
                                onSelect={(opt) =>
                                  setStudyForm((prev) => ({
                                    ...prev,
                                    department: opt?.label || "",
                                  }))
                                }
                              />
                            </div>
                            <div className="study-form-field">
                              <label>
                                Status <span className="mandatory">*</span>
                              </label>
                              <Dropdown
                                options={statusDropdownOptions}
                                isMulti={false}
                                disabled={!canMutateStudy}
                                selected={
                                  statusDropdownOptions.find(
                                    (opt) =>
                                      String(opt.value).toLowerCase() ===
                                      String(studyForm.status || "").toLowerCase(),
                                  ) ||
                                  (studyForm.status
                                    ? {
                                        value: studyForm.status,
                                        label:
                                          String(studyForm.status)
                                            .toLowerCase()
                                            .startsWith("archiv")
                                            ? "Archived"
                                            : studyForm.status,
                                      }
                                    : null)
                                }
                                onSelect={(opt) =>
                                  setStudyForm((prev) => ({
                                    ...prev,
                                    status: opt?.value || "",
                                  }))
                                }
                              />
                            </div>
                            <div className="study-form-field">
                              <label>Eudract Number</label>
                              <input
                                type="text"
                                className="form-control"
                                name="eudractNumber"
                                value={studyForm.eudractNumber}
                                onChange={handleStudyInput}
                                disabled={!canMutateStudy}
                                readOnly={!canMutateStudy}
                              />
                            </div>
                            <div className="study-form-field">
                              <label>R&D Number</label>
                              <input
                                type="text"
                                className="form-control"
                                name="rdNumber"
                                value={studyForm.rdNumber}
                                onChange={handleStudyInput}
                                disabled={!canMutateStudy}
                                readOnly={!canMutateStudy}
                              />
                            </div>
                            <div className="study-form-field">
                              <label>Sponsor Name</label>
                              <input
                                type="text"
                                className="form-control"
                                name="sponsorName"
                                value={studyForm.sponsorName}
                                onChange={handleStudyInput}
                                disabled={!canMutateStudy}
                                readOnly={!canMutateStudy}
                              />
                            </div>
                            <div className="study-form-field">
                              <label>IRAS Number</label>
                              <input
                                type="text"
                                className="form-control"
                                name="irasNumber"
                                value={studyForm.irasNumber}
                                onChange={handleStudyInput}
                                disabled={!canMutateStudy}
                                readOnly={!canMutateStudy}
                              />
                            </div>

                            <StudyMetadataFields
                              values={studyForm}
                              onChange={handleStudyMetadataChange}
                              isCreating={false}
                              disabled={!canMutateStudy}
                            />
                          </div>

                          <div className="study-coordinator-accordion">
                            <Accordion
                              title="Coordinator Information"
                              defaultOpen={
                                !!(
                                  studyForm.coordinatorName ||
                                  studyForm.coordinatorEmail ||
                                  studyForm.coordinatorContact
                                )
                              }
                              headerBg="#f8fafc"
                            >
                            <div className="study-form-grid study-form-grid--coordinator">
                              <div className="study-form-field">
                                <label>Coordinator Name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="coordinatorName"
                                  value={studyForm.coordinatorName}
                                  onChange={handleStudyInput}
                                  disabled={!canMutateStudy}
                                  readOnly={!canMutateStudy}
                                />
                              </div>
                              <div className="study-form-field">
                                <label>Coordinator Email</label>
                                <input
                                  type="email"
                                  className="form-control"
                                  name="coordinatorEmail"
                                  value={studyForm.coordinatorEmail}
                                  onChange={handleStudyInput}
                                  disabled={!canMutateStudy}
                                  readOnly={!canMutateStudy}
                                />
                              </div>
                              <div className="study-form-field">
                                <label>Coordinator Contact</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  name="coordinatorContact"
                                  value={studyForm.coordinatorContact}
                                  onChange={handleStudyInput}
                                  disabled={!canMutateStudy}
                                  readOnly={!canMutateStudy}
                                />
                              </div>
                            </div>
                          </Accordion>
                          </div>
                        </div>

                        <StudyAssignedStaffPanel
                          staffRows={siteFilteredStaff}
                          selectedSites={selectedSites}
                          assignedStaffIds={selectedStaffIds}
                          onToggleStaff={
                            canMutateStudy ? handleToggleStudyStaff : undefined
                          }
                          readOnly={!canMutateStudy}
                          hasSitesSelected={selectedSiteIds.length > 0}
                        />
                      </div>
                    ) : (
                      <p className="text-muted">
                        No study data available. Please open this page from
                        the Study Management list.
                      </p>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <StudyArchiveWizard
        open={archiveWizardOpen}
        studyRow={
          study ||
          initialStudy || {
            StudyId: studyId,
            StudyName: studyForm.studyTitle || studyForm.studyName,
            StudyNumber: studyForm.studyNumber,
            StudyStatus: studyForm.status,
          }
        }
        userData={userData}
        siteIds={selectedSites}
        showAlert={showAlert}
        onClose={() => setArchiveWizardOpen(false)}
        onArchived={() => {
          setArchiveWizardOpen(false);
          setStudyForm((prev) => ({ ...prev, status: "Archived" }));
          setStudy((prev) =>
            prev
              ? { ...prev, StudyStatus: "Archived", InActive: 1, Archive: 1 }
              : prev,
          );
        }}
      />
    </div>
  );
};

export default StudyProfile;

